import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MousePointerClick, Download, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Human-readable labels for the CTA IDs the frontend emits via
// trackCtaClick(). Add new entries here as you instrument more tiles.
const CTA_LABELS = {
  "home-ai-studio-tile": "AI Media Studio (home)",
  "home-ai-toolkit-tile": "AI Toolkit (home)",
  "home-aria-agents-tile": "ARIA Marketplace (home)",
};


export default function TopCtaClicks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/cta-clicks")
      .then((r) => setData(r.data))
      .catch(() => setData({
        totals: [], recent: [], grand_total: 0,
        grand_signups: 0, overall_conversion_pct: 0,
      }))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = () => {
    // Lightweight client-side CSV — we don't have a server export
    // endpoint for cta-clicks yet (deferred). Build from the data
    // we already loaded.
    if (!data?.totals?.length) {
      toast.error("Nothing to export yet");
      return;
    }
    const header = "cta_id,clicks,signups,conversion_pct\n";
    const body = data.totals
      .map((r) => `${r.cta_id},${r.count},${r.signups},${r.conversion_pct ?? ""}`)
      .join("\n");
    const url = URL.createObjectURL(new Blob([header + body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `cta_clicks_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const top3 = (data?.totals || []).slice(0, 3);
  const grandTotal = data?.grand_total || 0;
  const grandSignups = data?.grand_signups || 0;
  const overall = data?.overall_conversion_pct || 0;
  const maxClicks = top3[0]?.count || 1;

  return (
    <motion.div
      data-testid="top-cta-clicks-widget"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="glass-card p-6 border border-white/10 mb-6"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <MousePointerClick size={18} className="text-gold" />
          <div>
            <h4 className="text-white font-body text-sm font-medium">Top CTA Clicks</h4>
            <p className="text-white/30 text-[10px] font-body uppercase tracking-wider mt-0.5">
              {grandTotal.toLocaleString()} clicks · {grandSignups.toLocaleString()} signups · {overall}% overall
            </p>
          </div>
        </div>
        <button
          data-testid="top-cta-clicks-export-csv"
          onClick={downloadCsv}
          className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-[10px] font-body uppercase tracking-wider border border-gold/30 hover:border-gold/60 px-2.5 py-1 transition-colors"
        >
          <Download size={11} /> CSV
        </button>
      </div>

      {loading ? (
        <p data-testid="top-cta-clicks-loading" className="text-white/30 text-xs font-body">Loading…</p>
      ) : top3.length === 0 ? (
        <div data-testid="top-cta-clicks-empty" className="py-2">
          <p className="text-white/30 text-xs font-body">
            No CTA clicks yet. Once visitors click homepage tiles, they'll show here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="top-cta-clicks-list">
          {top3.map((row, i) => {
            const conv = row.conversion_pct;
            const widthPct = Math.round((row.count / maxClicks) * 100);
            const hasConv = conv !== null && conv !== undefined;
            const goodConv = hasConv && conv >= 3;
            return (
              <div key={row.cta_id} data-testid={`top-cta-row-${row.cta_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-gold/60 font-body text-[10px] w-3">#{i + 1}</span>
                    <span className="text-white font-body text-xs">
                      {CTA_LABELS[row.cta_id] || row.cta_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-body">
                    <span className="text-gold">{row.count.toLocaleString()}</span>
                    <span className="text-white/30">{row.signups || 0} signups</span>
                    {hasConv && (
                      <span
                        data-testid={`top-cta-conv-${row.cta_id}`}
                        className={`inline-flex items-center gap-0.5 ${goodConv ? "text-emerald-400" : "text-rose-400"}`}
                        title="conversion rate"
                      >
                        {goodConv ? <ArrowUp size={10} strokeWidth={2.5} /> : <ArrowDown size={10} strokeWidth={2.5} />}
                        {conv}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-1 bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${widthPct}%` }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="h-full bg-gradient-to-r from-gold/60 to-gold"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
